import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { useSiteContent, resolveImage } from "@/context/SiteContentContext";

export default function Hero() {
  const content = useSiteContent();
  const h = content?.hero;
  if (!h) return null;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 lg:pt-36 pb-20 lg:pb-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        {h.background_image && (
          <img
            src={resolveImage(h.background_image)}
            alt=""
            className="absolute right-0 top-0 w-[80%] h-full object-cover opacity-70"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-sky-50 via-sky-50/80 to-transparent" />
        <div className="absolute inset-0 grid-texture opacity-50" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 backdrop-blur-xl border border-white/60 rounded-full text-xs tracking-[0.2em] uppercase text-slate-700 font-display"
            data-testid="hero-badge"
          >
            <Sparkles size={14} className="text-orange-600" />
            {h.badge}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif-sc mt-6 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] font-bold text-slate-900 tracking-tight"
            data-testid="hero-heading"
          >
            {h.name_en}<span className="text-orange-600">.</span>
            <span className="block mt-2 text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-700 whitespace-pre-line">
              {h.subtitle}
            </span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 max-w-xl"
          >
            <div className="h-px w-24 bg-orange-600 mb-6" />
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed whitespace-pre-line">
              {h.description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 rounded-full text-sm font-medium tracking-wide transition-all active:scale-95 shadow-lg shadow-orange-600/20"
            >
              {h.cta_primary}
              <ArrowDown size={16} className="rotate-[-90deg]" />
            </a>
            <a
              href="#about"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 text-slate-900 hover:text-orange-600 px-2 py-4 text-sm font-medium border-b-2 border-slate-900 hover:border-orange-600 transition-colors"
            >
              {h.cta_secondary}
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative aspect-[4/5] w-full max-w-md mx-auto"
          >
            <div className="absolute -top-4 -right-4 w-full h-full border-2 border-orange-600 rounded-sm" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-sky-200/70 backdrop-blur-sm rounded-sm" />
            {h.portrait_image && (
              <img
                src={resolveImage(h.portrait_image)}
                alt={h.founder_name}
                data-testid="hero-portrait"
                className="relative w-full h-full object-cover transition-all duration-700 rounded-sm shadow-2xl shadow-slate-900/20"
              />
            )}
            <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-xl p-4 border border-white/60 rounded-sm">
              <div className="text-xs tracking-[0.25em] uppercase text-slate-500 font-display">
                {h.founder_label}
              </div>
              <div className="font-serif-sc text-xl text-slate-900 mt-1">{h.founder_name}</div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-slate-500">
        <span className="text-[10px] tracking-[0.3em] uppercase font-display">
          {content?.sections?.scroll_hint || "Scroll to explore"}
        </span>
        <ArrowDown size={14} className="animate-bounce" />
      </div>
    </section>
  );
}
